export function normalizeStr(str) {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
}

function matchWords(label, words) {
  const norm = normalizeStr(label)
  return words.every((w) => norm.includes(w))
}

function filterEntry(entry, words, sectionLabel) {
  // the section label is part of each entry's searchable text, so "compute" matches
  // every entry under the "Compute" section, and "compute ec2" still narrows to EC2
  const prefix = sectionLabel ? `${sectionLabel} ` : ''
  if (entry.type === 'link') {
    return matchWords(`${prefix}${entry.label}`, words) ? entry : null
  }
  if (matchWords(`${prefix}${entry.label}`, words)) return { ...entry, searchExpanded: true }
  const items = (entry.items ?? []).filter((item) => matchWords(`${prefix}${entry.label} ${item.label}`, words))
  return items.length > 0 ? { ...entry, items, searchExpanded: true } : null
}

// prefix carries the admin block's own label, so "admin users" still reaches an
// item whose section is "System"
function filterSection(section, words, prefix = '') {
  const entries = section.entries.map((e) => filterEntry(e, words, `${prefix}${section.label}`)).filter(Boolean)
  return entries.length > 0 ? { ...section, entries } : null
}

export function filterMenu(menu, query) {
  if (!menu) return menu
  const trimmed = (query ?? '').trim()
  if (!trimmed) return menu

  const words = normalizeStr(trimmed).split(/\s+/)

  const provider_menus = Object.fromEntries(
    (menu.providers ?? []).map((p) => [
      p,
      (menu.provider_menus[p] ?? []).map((s) => filterSection(s, words)).filter(Boolean),
    ]),
  )

  let admin = null
  if (menu.admin) {
    if (matchWords(menu.admin.label, words)) {
      admin = { ...menu.admin, searchExpanded: true }
    } else {
      const sections = (menu.admin.sections ?? [])
        .map((s) => filterSection(s, words, `${menu.admin.label} `))
        .filter(Boolean)
      if (sections.length > 0) admin = { ...menu.admin, sections, searchExpanded: true }
    }
  }

  return { ...menu, provider_menus, admin }
}

export function flattenMenuResults(menu, activeProvider = null) {
  if (!menu) return []
  const results = []

  const providers = activeProvider ? [activeProvider] : (menu.providers ?? [])
  // admin is never scoped out: its pages belong to no provider
  const sections = [...providers.flatMap((p) => menu.provider_menus?.[p] ?? []), ...(menu.admin?.sections ?? [])]

  for (const section of sections) {
    for (const entry of section.entries) {
      if (entry.type === 'link') {
        results.push({
          label: entry.label,
          href: entry.href,
          groupLabel: null,
          section: section.label,
          icon: entry.icon,
        })
      } else {
        for (const item of entry.items ?? []) {
          results.push({
            label: item.label,
            href: item.href,
            groupLabel: entry.label,
            section: section.label,
            icon: entry.icon,
          })
        }
      }
    }
  }

  return results
}

// Orders flat results by where the query matched, most specific first:
// 1) item name, 2) group name, 3) section name, 4) only a combined match.
// Stable within each tier (preserves the menu's natural order).
export function rankResults(results, query) {
  const words = normalizeStr((query ?? '').trim())
    .split(/\s+/)
    .filter(Boolean)
  if (!words.length) return results
  const tier = (r) => {
    if (matchWords(r.label, words)) return 1
    if (r.groupLabel && matchWords(r.groupLabel, words)) return 2
    if (r.section && matchWords(r.section, words)) return 3
    return 4
  }
  return results
    .map((r, i) => ({ r, i, tier: tier(r) }))
    .sort((a, b) => a.tier - b.tier || a.i - b.i)
    .map((x) => x.r)
}
